import type React from 'react';
import { itemStats, minToTime, type PlacedItem } from './calendarLayout';
import { calType, typeVars, type CalItem, type CalSession } from './calendarTypes';

/* The pieces a time-scaled column is built from: the event card, the run card,
 * the "+N" overflow button and the occupancy rail.
 *
 * All geometry is absolute against a column whose height is
 * (endHour - startHour) * hourPx, so 07:00 sits at the same y in every column
 * and in every mode. */

export interface Geometry {
  /** First hour drawn, e.g. 6. */
  startHour: number;
  /** Pixels per hour — the density control (48 / 72 / 104). */
  hourPx: number;
}

export const topFor = (min: number, g: Geometry) => ((min - g.startHour * 60) / 60) * g.hourPx + 1;
export const heightFor = (start: number, end: number, g: Geometry) =>
  Math.max(18, ((end - start) / 60) * g.hourPx - 2);

/** Card frame: lane share of the column, inset 2px each side. */
export function laneStyle(p: PlacedItem, g: Geometry): React.CSSProperties {
  return {
    top: topFor(p.it.start, g),
    height: heightFor(p.it.start, p.it.end, g),
    left: `calc(${(p.lane / p.lanes) * 100}% + 2px)`,
    width: `calc(${100 / p.lanes}% - 4px)`,
  };
}

/** Occupancy class for one slot: full, half, some, empty, cancelled. */
function fillClass(s: CalSession): string {
  if (s.status === 'cancelled') return 'cx';
  const f = s.cap ? s.booked / s.cap : 0;
  return f >= 1 ? 'f3' : f >= 0.5 ? 'f2' : f > 0 ? 'f1' : 'f0';
}

// ─── Event card ──────────────────────────────────────────────────────────────

/**
 * One session.
 *
 * Content appears as the card gets taller, so a 20-minute consult shows a
 * legible title instead of four clipped lines. A 1:1 session never says "Full"
 * — "Full" on a capacity of one is noise; the member's name is the information.
 */
export function EventCard({ s, style, selected, width, onPick, onActivate, interaction, extraClass }: {
  s: CalSession;
  style: React.CSSProperties;
  selected?: boolean;
  /** Rendered width in px — decides whether the longer strings fit. */
  width: number;
  onPick?: () => void;
  /** Keyboard activation. Defaults to onPick; the Today grid opens a session
   *  from its pointer-up handler instead, so it passes this on its own to keep
   *  Enter and Space working without firing the open twice. */
  onActivate?: () => void;
  /** Drag handlers from the Today grid; omitted elsewhere. */
  interaction?: React.HTMLAttributes<HTMLDivElement> & { style?: React.CSSProperties };
  extraClass?: string;
}) {
  const T = calType(s.type);
  const h = Number(style.height) || 0;
  const cancelled = s.status === 'cancelled';
  const full = s.cap > 1 && s.booked >= s.cap && !cancelled;
  const fill = s.cap ? Math.min(1, s.booked / s.cap) : 0;

  const cls = [
    'a-ev',
    s.past ? 'past' : '',
    s.live ? 'live' : '',
    cancelled ? 'cx' : '',
    selected ? 'sel' : '',
    extraClass || '',
  ]
    .filter(Boolean)
    .join(' ');

  const capacityLine = full ? (
    <b className="full">Full{s.wait ? ` +${s.wait}` : ''}</b>
  ) : s.cap === 1 ? (
    s.member || (s.booked ? 'Booked' : 'Open')
  ) : (
    `${s.booked}/${s.cap}`
  );

  const label = `${s.name}, ${minToTime(s.start)} to ${minToTime(s.end)}, ${
    s.cap === 1 ? (s.booked ? s.member || 'booked' : 'open') : `${s.booked} of ${s.cap} booked`
  }${cancelled ? ', cancelled' : s.past ? ', past' : s.live ? ', in progress' : ''}${
    s.needsStaff ? ', needs staff' : ''
  }`;

  return (
    <div
      {...interaction}
      className={cls}
      style={{ ...style, ...typeVars(T), ...(interaction?.style || {}) }}
      onClick={e => { e.stopPropagation(); onPick?.(); }}
      role="button"
      tabIndex={0}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); (onActivate ?? onPick)?.(); }
      }}
      aria-label={label}
      title={`${s.name} · ${minToTime(s.start)}–${minToTime(s.end)}`}
    >
      {/* Reveal thresholds are measured against the real line boxes (title 15px,
          each meta line 14px, 7px of padding), not round numbers — at Compact
          density a 60-minute card is 46px and the design's 46 clipped its last
          line. */}
      <span className="a-ev-t">{s.name}</span>
      {h >= 32 && (
        <span className="a-ev-m">{width > 90 ? `${minToTime(s.start)}–${minToTime(s.end)}` : minToTime(s.start)}</span>
      )}
      {h >= 50 && <span className="a-ev-m">{capacityLine}</span>}
      {h >= 68 && width > 90 && s.type !== 'pt' && (
        <span className="a-ev-m a-ev-sub">{[s.res, s.who].filter(Boolean).join(' · ')}</span>
      )}
      {/* Too short for the capacity line, but the room is gone — say so with a dot. */}
      {h < 50 && full && <span className="a-ev-dot" />}
      {s.needsStaff && !full && <span className="a-ev-flag" title="Needs staff" />}
      {s.cap > 1 && (
        <span className="a-ev-meter">
          <span style={{ width: `${fill * 100}%` }} />
        </span>
      )}
    </div>
  );
}

// ─── Run card ────────────────────────────────────────────────────────────────

/**
 * A merged run of slots, full size (day-by-resource mode).
 *
 * The segments are flexed by duration so they line up exactly with the time
 * grid: a court's 07:00 slot sits on the 07:00 line. Occupancy is read from
 * the fill, not from text, so a four-hour court reads at a glance.
 */
export function RunCard({ run, style, selected, width, onPick }: {
  run: Extract<CalItem, { kind: 'run' }>;
  style: React.CSSProperties;
  selected?: boolean;
  width: number;
  onPick?: () => void;
}) {
  const T = calType(run.type);
  const total = run.end - run.start;
  const h = Number(style.height) || 0;
  const st = itemStats(run);

  return (
    <div
      className={`a-run${run.s.past ? ' past' : ''}${selected ? ' sel' : ''}`}
      style={{ ...style, ...typeVars(T) }}
      onClick={e => { e.stopPropagation(); onPick?.(); }}
      role="button"
      tabIndex={0}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPick?.(); }
      }}
      aria-label={`${run.s.name}, ${run.s.res}, ${minToTime(run.start)} to ${minToTime(run.end)}, ${run.slots.length} slots, ${st.booked} of ${st.cap} booked`}
      title={`${run.s.name} · ${run.s.res} · ${minToTime(run.start)}–${minToTime(run.end)}`}
    >
      {run.slots.map((x, i) => {
        const segH = ((x.end - x.start) / total) * h;
        return (
          <div
            key={x.id}
            className={`a-rseg ${fillClass(x)}${x.past ? ' sp' : ''}`}
            style={{ flex: x.end - x.start }}
            title={`${minToTime(x.start)} · ${x.booked}/${x.cap}`}
          >
            {segH >= 17 && (
              <span className="a-rseg-l">
                {i === 0 && width > 70 ? <b>{run.s.res}</b> : <span>{minToTime(x.start)}</span>}
                <span className="a-rseg-f">{x.cap === 1 ? (x.booked ? '●' : '○') : `${x.booked}/${x.cap}`}</span>
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Occupancy rail (week mode) ──────────────────────────────────────────────

/** In week mode a run takes no card lane at all — it becomes a 5px strip at the
 *  column's right edge. Seven days of courts would otherwise bury every class. */
export function RailStrip({ run, top, height, left, selected, onPick }: {
  run: Extract<CalItem, { kind: 'run' }>;
  top: number;
  height: number;
  left: number;
  selected?: boolean;
  onPick?: () => void;
}) {
  const T = calType(run.type);
  return (
    <button
      className={`a-rl${run.s.past ? ' past' : ''}${selected ? ' sel' : ''}`}
      style={{ top, height, left, ...typeVars(T) }}
      onClick={e => { e.stopPropagation(); onPick?.(); }}
      title={`${run.s.name} · ${run.s.res} · ${minToTime(run.start)}–${minToTime(run.end)}`}
      aria-label={`${run.s.name}, ${run.s.res}, ${minToTime(run.start)} to ${minToTime(run.end)}`}
    >
      {run.slots.map(x => (
        <span key={x.id} className={fillClass(x)} style={{ flex: x.end - x.start }} />
      ))}
    </button>
  );
}

// ─── "+N" overflow ───────────────────────────────────────────────────────────

export function MoreButton({ more, style, onOpen }: {
  more: Extract<CalItem, { kind: 'more' }>;
  style: React.CSSProperties;
  onOpen: (more: Extract<CalItem, { kind: 'more' }>, rect: DOMRect) => void;
}) {
  const h = Number(style.height) || 0;
  const types = [...new Set(more.items.map(i => (i.kind === 'more' ? 'class' : i.type)))];
  return (
    <button
      className="a-more"
      style={style}
      onClick={e => { e.stopPropagation(); onOpen(more, e.currentTarget.getBoundingClientRect()); }}
      aria-label={`${more.items.length} more sessions at ${minToTime(more.start)}`}
    >
      <b>+{more.items.length}</b>
      {h > 34 && (
        <span className="dots">
          {types.map(t => <i key={t} style={{ background: calType(t).c }} />)}
        </span>
      )}
    </button>
  );
}

export function MorePopover({ more, rect, onPick, onClose }: {
  more: Extract<CalItem, { kind: 'more' }>;
  rect: DOMRect;
  onPick: (it: CalItem) => void;
  onClose: () => void;
}) {
  const left = Math.max(8, Math.min(window.innerWidth - 300, rect.right + 6));
  const top = Math.max(12, Math.min(window.innerHeight - 60 - more.items.length * 46, rect.top));
  return (
    <>
      <div className="acal-scrim0" onClick={onClose} />
      <div className="acal-mpop" style={{ left, top }} role="dialog" aria-label="Hidden sessions">
        <div className="h">
          {minToTime(more.start)}–{minToTime(more.end)} · {more.items.length} more
        </div>
        {more.items.map(it => {
          if (it.kind === 'more') return null;
          const T = calType(it.type);
          const st = itemStats(it);
          return (
            <button key={it.id} onClick={() => { onPick(it); onClose(); }} style={typeVars(T)}>
              <i />
              <span className="t">{minToTime(it.start)}</span>
              <span className="n">
                <span>{it.kind === 'run' ? it.s.res : it.s.name}</span>
                <em>{it.s.cap === 1 ? it.s.member || 'Open' : it.s.res}</em>
              </span>
              <span className="f">{st.cap === 1 ? (st.booked ? 'Booked' : 'Open') : `${st.booked}/${st.cap}`}</span>
            </button>
          );
        })}
      </div>
    </>
  );
}
